import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, Minus, Plus, ChevronLeft, Ruler, Truck, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useGeo } from '../contexts/GeoContext';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';
import InternationalShippingNotice from '../components/InternationalShippingNotice';
import { getSwatchStyle } from '../lib/colorMap';
import { hero as optHero, thumb as optThumb } from '../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sizeGuideData = {
  tops: [
    { size: 'XS', chest: '32-34', waist: '26-28' },
    { size: 'S', chest: '34-36', waist: '28-30' },
    { size: 'M', chest: '38-40', waist: '32-34' },
    { size: 'L', chest: '42-44', waist: '36-38' },
    { size: 'XL', chest: '46-48', waist: '40-42' },
    { size: 'XXL', chest: '50-52', waist: '44-46' },
  ],
  bottoms: [
    { size: '28', waist: '28', hip: '36', inseam: '30' },
    { size: '30', waist: '30', hip: '38', inseam: '30' },
    { size: '32', waist: '32', hip: '40', inseam: '32' },
    { size: '34', waist: '34', hip: '42', inseam: '32' },
    { size: '36', waist: '36', hip: '44', inseam: '32' },
  ],
};

// Size conversion maps
const SHOE_SIZE_CONVERT = {
  US_TO_UK: { '5': '3', '5.5': '3.5', '6': '4', '6.5': '4.5', '7': '5', '7.5': '5.5', '8': '6', '8.5': '6.5', '9': '7', '9.5': '7.5', '10': '8', '10.5': '8.5', '11': '9', '11.5': '9.5', '12': '10', '13': '11', '14': '12' },
  US_TO_EU: { '5': '37.5', '5.5': '38', '6': '38.5', '6.5': '39', '7': '40', '7.5': '40.5', '8': '41', '8.5': '42', '9': '42.5', '9.5': '43', '10': '44', '10.5': '44.5', '11': '45', '11.5': '45.5', '12': '46', '13': '47.5', '14': '48.5' },
};
const CLOTHING_SIZE_CONVERT = {
  US_TO_UK: { 'XS': 'XS', 'S': 'S', 'M': 'M', 'L': 'L', 'XL': 'XL', 'XXL': 'XXL', '2XL': '2XL', '3XL': '3XL' },
  US_TO_EU: { 'XS': '44', 'S': '46', 'M': '48', 'L': '50', 'XL': '52', 'XXL': '54', '2XL': '56', '3XL': '58' },
};

// Detect if sizes are shoe sizes (numeric) or clothing sizes (letters)
const isShoeSize = (sizes) => sizes?.length > 0 && /^[\d.]+$/.test(sizes[0]);

const convertSize = (usSize, isShoe, system) => {
  if (system === 'US') return usSize;
  const map = isShoe
    ? (system === 'UK' ? SHOE_SIZE_CONVERT.US_TO_UK : SHOE_SIZE_CONVERT.US_TO_EU)
    : (system === 'UK' ? CLOTHING_SIZE_CONVERT.US_TO_UK : CLOTHING_SIZE_CONVERT.US_TO_EU);
  return map[usSize] || usSize;
};

// Countries that use UK sizing (by country code)
const UK_SIZE_CODES = ['GB', 'AU', 'NZ', 'ZA', 'IN', 'IE'];
// Countries that use EU sizing (by country code)
const EU_SIZE_CODES = ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'PT', 'AT', 'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'GR', 'RO', 'HU', 'CH'];

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { formatPrice, country, countryCode, region, currency, loaded: geoLoaded } = useGeo();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [sizeSystem, setSizeSystem] = useState('US'); // US, UK, EU
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  // Auto-detect size system from country code
  useEffect(() => {
    if (geoLoaded && countryCode) {
      if (UK_SIZE_CODES.includes(countryCode)) setSizeSystem('UK');
      else if (EU_SIZE_CODES.includes(countryCode)) setSizeSystem('EU');
      else setSizeSystem('US');
    }
  }, [geoLoaded, countryCode]);

  const inWishlist = product ? isInWishlist(product.id) : false;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/products/${id}`);
        setProduct(data);
        
        // Set defaults
        if (data.sizes?.length > 0) setSelectedSize(data.sizes[0]);
        if (data.colors?.length > 0) setSelectedColor(data.colors[0]);

        // Fetch color-matched suggestions across categories
        const suggestions = await axios.get(`${API}/products/${id}/suggestions`);
        setRelatedProducts(suggestions.data.suggestions || []);
      } catch (err) {
        console.error('Error fetching product:', err);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return;
    }

    setAddingToCart(true);
    const primaryColor = product.colors?.[0] || '';
    const result = await addToCart(product.id, quantity, selectedSize, primaryColor);
    setAddingToCart(false);

    if (result.success) {
      toast.success('Added to cart!');
    } else {
      toast.error(result.error);
    }
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    if (inWishlist) {
      const result = await removeFromWishlist(product.id);
      if (result.success) toast.success('Removed from wishlist');
    } else {
      const result = await addToWishlist(product.id);
      if (result.success) toast.success('Added to wishlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-[3/4] border-2 border-[#0A0A0A] skeleton"></div>
            <div className="space-y-4">
              <div className="h-8 w-1/4 skeleton"></div>
              <div className="h-12 w-3/4 skeleton"></div>
              <div className="h-6 w-1/3 skeleton"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const discountedPrice = product.is_promo && product.promo_percentage
    ? product.price * (1 - product.promo_percentage / 100)
    : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#525252] hover:text-[#0A0A0A]">Home</Link>
            <span className="text-[#525252]">/</span>
            <Link to="/products" className="text-[#525252] hover:text-[#0A0A0A]">Products</Link>
            <span className="text-[#525252]">/</span>
            <Link to={`/products?category=${product.category}`} className="text-[#525252] hover:text-[#0A0A0A] capitalize">
              {product.category}
            </Link>
            <span className="text-[#525252]">/</span>
            <span className="font-semibold truncate">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm font-semibold mb-6 hover:underline"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </button>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="sticky top-24">
              {/* Main Image */}
              <div className="aspect-[3/4] border-2 border-[#0A0A0A] overflow-hidden mb-4 relative">
                {product.is_promo && product.promo_percentage && (
                  <span className="badge-promo absolute top-4 left-4 z-10">
                    -{product.promo_percentage}%
                  </span>
                )}
                <img
                  src={optHero(product.images?.[activeImage] || product.images?.[0]) || '/placeholder.jpg'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Thumbnails */}
              {product.images?.length > 1 && (
                <div className="flex gap-2">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(idx)}
                      className={`w-20 h-24 border-2 overflow-hidden ${
                        activeImage === idx ? 'border-[#0A0A0A]' : 'border-transparent'
                      }`}
                    >
                      <img src={optThumb(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <p className="text-sm text-[#525252] uppercase tracking-wider mb-2">
              {product.category}
            </p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {product.name}
            </h1>

            {/* Price */}
            <div className="flex flex-col gap-1 mb-6">
              <div className="flex items-center gap-3">
                {discountedPrice ? (
                  <>
                    <span className="text-3xl font-bold price-sale">
                      {formatPrice(discountedPrice)}
                    </span>
                    <span className="text-xl price-original">
                      {formatPrice(product.price)}
                    </span>
                  </>
                ) : (
                  <span className="text-3xl font-bold">
                    {formatPrice(product.price)}
                  </span>
                )}
              </div>
              {geoLoaded && country && currency !== 'USD' && (
                <span className="text-xs text-[#525252]">
                  Price shown in {currency} for {region ? `${region}, ` : ''}{country} &middot; Excl. postage
                </span>
              )}
              {geoLoaded && currency === 'USD' && country && (
                <span className="text-xs text-[#525252]">
                  {region ? `${region}, ` : ''}{country} &middot; Excl. postage
                </span>
              )}
            </div>

            <p className="text-[#525252] leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Dress-Up notice — this listing is a styled flat-lay showing multiple pieces together */}
            {product.is_dress_up && (
              <div
                data-testid="dress-up-notice"
                className="mb-6 border-2 border-[#0A0A0A] bg-[#FFC700] p-3 flex items-start gap-3 shadow-[3px_3px_0px_#0A0A0A]"
              >
                <span className="inline-block bg-[#0A0A0A] text-[#FFC700] text-[10px] font-black uppercase tracking-[0.25em] px-2 py-1 mt-0.5 whitespace-nowrap">
                  Dress Up
                </span>
                <p className="text-xs text-[#0A0A0A] leading-snug">
                  The image shows the <strong>complete styled fit</strong>. This listing is for the item in the product title only — matching pieces (cap, jacket, etc.) are available separately. Ask Shannon to pull the full look in one tap.
                </p>
              </div>
            )}

            {/* Colorway (sold as seen – full multi-tone description, no picker) */}
            {product.colors?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <span
                    className="w-8 h-8 rounded-full border-2 border-[#0A0A0A] shadow-[2px_2px_0px_#0A0A0A] flex-shrink-0"
                    style={getSwatchStyle(product.colors.join('/'))}
                    title={product.colors.join(' / ')}
                    data-testid="product-color-swatch"
                  />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#525252]">Colourway</p>
                    <p className="font-semibold text-sm truncate" data-testid="product-color-name">{product.colors.join(' / ')}</p>
                  </div>
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-[#999] border border-[#E5E5E5] px-2 py-1 whitespace-nowrap">Sold as seen</span>
                </div>
              </div>
            )}

            {/* Size Selector */}
            {product.sizes?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">Size ({sizeSystem}): <span className="font-normal">{convertSize(selectedSize, isShoeSize(product.sizes), sizeSystem)}</span></p>
                    {/* Size system toggle */}
                    <div className="flex border border-[#0A0A0A] text-[10px] font-bold">
                      {['US', 'UK', 'EU'].map(sys => (
                        <button
                          key={sys}
                          onClick={() => setSizeSystem(sys)}
                          className={`px-2 py-0.5 transition-colors ${sizeSystem === sys ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F5F5F5]'}`}
                          data-testid={`size-system-${sys.toLowerCase()}`}
                        >
                          {sys}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-sm underline flex items-center gap-1" data-testid="size-guide-btn">
                        <Ruler className="w-4 h-4" />
                        Size Guide
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg border-2 border-[#0A0A0A] rounded-none">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          Size Guide
                        </DialogTitle>
                      </DialogHeader>
                      <div className="mt-4">
                        <h4 className="font-semibold mb-3">Measurements (inches)</h4>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b-2 border-[#0A0A0A]">
                              <th className="text-left py-2">Size</th>
                              <th className="text-left py-2">Chest</th>
                              <th className="text-left py-2">Waist</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sizeGuideData.tops.map((row) => (
                              <tr key={row.size} className="border-b border-[#E5E5E5]">
                                <td className="py-2 font-semibold">{row.size}</td>
                                <td className="py-2">{row.chest}</td>
                                <td className="py-2">{row.waist}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                      data-testid={`size-${size}`}
                    >
                      {convertSize(size, isShoeSize(product.sizes), sizeSystem)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <p className="font-semibold mb-3">Quantity</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center border-2 border-[#0A0A0A]">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="quantity-btn border-0"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-semibold" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="quantity-btn border-0"
                    data-testid="quantity-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-sm text-[#525252]">
                  {product.stock} in stock
                </span>
              </div>
            </div>

            {/* International shipping notice (IP-based, only renders outside US) */}
            <div className="mb-6">
              <InternationalShippingNotice variant="inline" />
            </div>

            {/* Actions */}
            <div className="flex gap-4 mb-8">
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart || product.stock === 0}
                data-testid="add-to-cart-btn"
                className="flex-1 bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none h-14 font-semibold text-base hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all disabled:opacity-50"
              >
                {addingToCart ? 'Adding...' : product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
              </Button>
              <Button
                onClick={handleWishlistToggle}
                variant="outline"
                data-testid="wishlist-btn"
                className={`w-14 h-14 border-2 border-[#0A0A0A] rounded-none ${
                  inWishlist ? 'bg-[#FAD4D4]' : ''
                } hover:bg-[#FAD4D4] transition-all`}
              >
                <Heart className={`w-5 h-5 ${inWishlist ? 'fill-current' : ''}`} />
              </Button>
            </div>

            {/* Features */}
            <div className="border-t-2 border-[#0A0A0A] pt-6 space-y-4">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5" />
                <span className="text-sm">Free shipping on orders over $100</span>
              </div>
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5" />
                <span className="text-sm">Easy 30-day returns</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Color-Matched Suggestions */}
        {relatedProducts.length > 0 && (
          <section className="mt-20">
            <h2 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              COMPLETE THE LOOK
            </h2>
            <p className="text-sm text-[#525252] mb-8">Products that match your color palette</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.slice(0, 8).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
